import { api } from '@creditsights/api-ui-web';
import { isUndefined, orderBy } from 'lodash';
import { LoanComparisonResults } from '../models/Securities';
import { addDateFilter } from '../utils/date.utils';
import { filterMapper } from '../utils/filter.utils';
import { formatLoanCompareData } from '../utils/loan-compare.utils';
import { fixCountryNamesBaseOnTags, mapSectors } from '../utils/tag.utils';
import { fetchFilters } from './filter.service';

const API_URL = 'common/v1/securities/comparison/loans';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchLoanCompareFilters = async () => {
  const filters = [
    {
      id: 'borrower',
      name: 'Borrowers',
    },
    {
      id: 'region',
      name: 'Region',
    },
    {
      id: 'sectors',
      name: 'Sector/Subsector',
    },
    {
      id: 'country_code',
      name: 'Country or Territory',
    },
    {
      id: 'arrangers.name',
      name: 'Arrangers',
    },
    {
      id: 'sponsors.name',
      name: 'Sponsors/Major Stockholders',
    },
    {
      id: 'purpose',
      name: 'Use of Proceeds (UOP)',
    },
    {
      id: 'phase',
      name: 'Phase',
    },
    {
      id: 'corporate_rating',
      name: "Moody's Corp Ratings",
    },
    {
      id: 'facility_rating',
      name: 'S&P Corp Ratings',
    },
  ];

  const promises = filters.map((filter) =>
    api.post(`${API_URL}/filters/${filter.id}`, {
      filters: {},
      take: 5000,
    })
  );

  const [results, tagFilters] = await Promise.all([Promise.all(promises), fetchFilters()]);
  const resp: any = {};

  results.forEach((result, index) => {
    const filter = filters[index];

    resp[filter.id] = {
      name: filter.name,
      data: filterMapper(result.data.filter((i) => !!i)),
    };
  });

  resp.country_code.data = fixCountryNamesBaseOnTags(resp.country_code.data, tagFilters.map);
  resp.sectors.data = mapSectors(tagFilters.data[6].options);
  return resp;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchLoanCompareData = async (sort, { startDate, endDate }, sizeFilter, filterConfig, pageSize, page = 1) => {
  const filters = {
    ...filterConfig,
  };

  if (!isUndefined(sizeFilter.min) || !isUndefined(sizeFilter.max)) {
    const size: any = {};

    if (!isUndefined(sizeFilter.min)) {
      size.min = sizeFilter.min;
    }

    if (!isUndefined(sizeFilter.max)) {
      size.max = sizeFilter.max;
    }

    filters.size = size;
  }

  addDateFilter(filters, 'launch_date', startDate, endDate);

  const { data } = await api.post<LoanComparisonResults>(API_URL, {
    filters,
    skip: (page - 1) * pageSize,
    take: pageSize,
    sort: sort.field,
    sortDesc: !sort.asc,
    fields: ['id', 'transaction_id', 'borrower', 'industry', 'purpose', 'launch_date', 'report_id', 'phase', 'arrangers', 'sponsors', 'size', 'cs_universal_id', 'entity_name', 'issuer', 'country_code', 'regions', 'region', 'sectors'],
  });

  data.results.forEach((item) => {
    // eslint-disable-next-line no-param-reassign
    item.arrangers = orderBy(item.arrangers, ['name']);
    // eslint-disable-next-line no-param-reassign
    item.sponsors = orderBy(item.sponsors, ['name']);
    // eslint-disable-next-line  no-param-reassign
    item.sectors = getSectorsFromIssuerImplies(item.issuer_implies);
  });

  return data;
};

export const getSectorsFromIssuerImplies = (data: string[]): string[] => {
  return data.reduce((accumulator, value) => {
    if (value.includes('sector')) {
      return [...accumulator, value.slice(7, value.length)];
    }
    return accumulator;
  }, [] as string[]);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchLoanCompareDetailsData = async (ids) => {
  if (ids.length === 0) {
    return {
      totalResults: 0,
      results: [],
    };
  }

  const { data } = await api.post(API_URL, {
    filters: {
      _id: {
        values: ids,
      },
    },
    take: 100,
  });

  return formatLoanCompareData(data.results);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchLoanCompareDetailsExport = async (ids: string[]) => {
  const url = `${API_URL}/export`;
  const response = await api.post(url, ids, { responseType: 'blob' });
  const contentDisposition = response.headers['content-disposition'];
  let fileName = 'ComparationList.xlsx';
  if (contentDisposition) {
    const filenamePar = 'filename="';
    const filenameParInd = contentDisposition.indexOf(filenamePar);
    if (filenameParInd > 0) {
      fileName = contentDisposition.substring(filenameParInd + filenamePar.length);
      if (fileName.charAt(fileName.length - 1) === '"') {
        fileName = fileName.substring(0, fileName.length - 1);
      }
    }
  }

  const { data } = response;

  return { data, fileName };
};
